const ENVELOPE_MARKER = 1;
const DEFAULT_VERSION = 1;

interface StoredEnvelope<T> {
	__rs: typeof ENVELOPE_MARKER;
	v: number;
	data: T;
}

export interface ReachStorageSchema<T = unknown> {
	version?: number;
	legacy?: 'text';
	migrate?: (fromVersion: number, data: unknown) => T;
	validate?: (data: unknown) => data is T;
}

function isStoredEnvelope(value: unknown): value is StoredEnvelope<unknown> {
	if (typeof value !== 'object' || value === null) {
		return false;
	}

	const envelope = value as Partial<StoredEnvelope<unknown>>;
	return (
		envelope.__rs === ENVELOPE_MARKER && typeof envelope.v === 'number' && 'data' in envelope
	);
}

function schemaFnSource(fn: { toString(): string } | undefined): string {
	return fn?.toString() ?? '';
}

function isSameSchema(left: ReachStorageSchema, right: ReachStorageSchema): boolean {
	return (
		(left.version ?? DEFAULT_VERSION) === (right.version ?? DEFAULT_VERSION) &&
		left.legacy === right.legacy &&
		schemaFnSource(left.migrate) === schemaFnSource(right.migrate) &&
		schemaFnSource(left.validate) === schemaFnSource(right.validate)
	);
}

class StorageI {
	private static assignedStorage: Storage | undefined;

	public static get storage(): Storage {
		return StorageI.assignedStorage ?? localStorage;
	}

	public static set storage(value: Storage) {
		StorageI.assignedStorage = value;
	}

	public static deleteOnStartupKeyBase = '__deleteOnStartup';

	public static persistentKeyBase = '__doNotWipe';

	private static readonly schemas = new Map<string, ReachStorageSchema>();

	public static asStartupKey(key: string): string {
		return `${StorageI.deleteOnStartupKeyBase}__${key}`;
	}

	public static asPersistentKey(key: string): string {
		if (
			key.startsWith(StorageI.persistentKeyBase) ||
			key.startsWith(StorageI.deleteOnStartupKeyBase)
		) {
			return key;
		}

		return `${StorageI.persistentKeyBase}__${key}`;
	}

	public static define<T>(key: string, schema: ReachStorageSchema<T>): void {
		const existing = this.schemas.get(key);
		if (existing) {
			if (!isSameSchema(existing, schema)) {
				throw new Error(`ReachStorage key already defined: ${key}`);
			}

			// The same module can run again on hot reload. Keep the new closures.
			this.schemas.set(key, schema);
			return;
		}

		this.schemas.set(key, schema);
	}

	public static removeStartupKeys(): void {
		StorageI.getKeys()
			.filter((k) => k.startsWith(StorageI.deleteOnStartupKeyBase))
			.forEach((k) => {
				StorageI.deleteItem(k);
			});
	}

	/**
	 * Stores an item given any key.
	 * @param key The key of the item to store.
	 * @param value The value of the item to store.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
	public static setItem<T>(key: string, value: T): void {
		const envelope: StoredEnvelope<T> = {
			__rs: ENVELOPE_MARKER,
			v: this.getSchemaVersion(key),
			data: value,
		};
		this.write(key, JSON.stringify(envelope));
	}

	/**
	 * @param key The key to the item to retrieve.
	 * @returns The value (or null if it is not stored) of the item with the given key.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
	public static getItem<T = unknown>(key: string): T | null {
		const stored = this.read(key);
		if (stored === null) {
			return null;
		}

		const schema = this.schemas.get(key);

		let parsed: unknown;
		try {
			parsed = JSON.parse(stored);
		} catch {
			if (schema?.legacy !== 'text') {
				return null;
			}

			return this.resolveValue<T>(key, stored, DEFAULT_VERSION, false);
		}

		if (isStoredEnvelope(parsed)) {
			return this.resolveValue<T>(key, parsed.data, parsed.v, true);
		}

		if (schema?.legacy === 'text') {
			return this.resolveValue<T>(key, stored, DEFAULT_VERSION, false);
		}

		return this.resolveValue<T>(key, parsed, DEFAULT_VERSION, false);
	}

	/**
	 * Removes the stored item with the given any key.
	 * @param key Key if the item to remove.
	 */
	public static deleteItem(key: string): void {
		this.delete(key);
	}

	public static getKeys(): string[] {
		const length = this.storage.length;
		return Array.from({ length }, (_, idx) => this.storage.key(idx)).filter(
			(key): key is string => key !== null
		);
	}

	/**
	 * Removes all the stored items.
	 */
	public static clear(): void {
		this.wipe();
	}

	private static getSchemaVersion(key: string): number {
		return this.schemas.get(key)?.version ?? DEFAULT_VERSION;
	}

	// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
	private static resolveValue<T>(
		key: string,
		data: unknown,
		fromVersion: number,
		isEnvelope: boolean
	): T | null {
		const schema = this.schemas.get(key);
		const expectedVersion = this.getSchemaVersion(key);

		if (fromVersion > expectedVersion) {
			return this.drop(key);
		}

		let resolved = data;
		if (fromVersion < expectedVersion) {
			if (!schema?.migrate) {
				return this.drop(key);
			}

			try {
				resolved = schema.migrate(fromVersion, data);
			} catch {
				return this.drop(key);
			}
		}

		if (schema?.validate && !schema.validate(resolved)) {
			return this.drop(key);
		}

		if (!isEnvelope || fromVersion < expectedVersion) {
			try {
				this.setItem(key, resolved as T);
			} catch {
				// Keep returning the resolved value even if rewriting the envelope fails.
			}
		}

		return resolved as T;
	}

	private static drop(key: string): null {
		this.deleteItem(key);
		return null;
	}

	private static read(key: string): string | null {
		const persistentKey = this.asPersistentKey(key);
		const stored = this.storage.getItem(persistentKey);
		if (stored !== null || persistentKey === key) {
			return stored;
		}

		return this.storage.getItem(key);
	}

	private static write(key: string, value: string): void {
		const persistentKey = this.asPersistentKey(key);
		this.storage.setItem(persistentKey, value);
		if (persistentKey !== key) {
			this.storage.removeItem(key);
		}
	}

	private static delete(key: string): void {
		const persistentKey = this.asPersistentKey(key);
		this.storage.removeItem(persistentKey);
		if (persistentKey !== key) {
			this.storage.removeItem(key);
		}
	}

	/**
	 * Every key is persisted for now, so logout keeps the same values as before clear() worked.
	 */
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private static wipe(): void {}
}

/**
 * Class that handles all the logic regarding Storage (localStorage).
 *
 * To use it, simply get the object's static methods like localStorage.
 */
export class ReachStorage extends StorageI {}
